
import { useState } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { useNavigate } from 'react-router-dom';
import { useActivityLogger } from '@/hooks/use-activity-logger';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon } from 'lucide-react';

const SignInForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signIn, refreshOrganizations } = useAuth();
  const { logActivity } = useActivityLogger();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }
    
    try {
      setIsSubmitting(true);
      await signIn(email, password);
      
      // Explicitly refresh organizations after successful login
      await refreshOrganizations();
      
      // Log the login activity after organizations are refreshed
      try {
        await logActivity({
          action: 'login',
          resourceType: 'user',
          details: { email }
        });
      } catch (error) {
        console.warn('Error logging activity:', error);
        // Don't block the login flow if activity logging fails
      }
      
      // Navigate to dashboard after successful login
      navigate('/');
      toast.success(`Welcome back, ${email}`);
    } catch (error) {
      console.error('Sign in error:', error);
      // No need to handle the error here as signIn already shows an error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Sign In</CardTitle>
        <CardDescription>
          Enter your email and password to access your account
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          <Alert variant="warning" className="mb-4">
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              New user registration is currently disabled. Please contact your administrator for access.
            </AlertDescription>
          </Alert>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col">
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default SignInForm;
